import { useState, useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import { MessageSquare, Shield, Phone, Clock, XCircle, Loader2 } from "lucide-react";
import { getBusinessInfo, type BusinessInfo } from "@/lib/booking-api";
import { BlobBackground } from "@/components/ui";
import { generateBlobColors } from "@/lib/branding-context";

// ════════════════════════════════════════════════════════════════════════════
// SMS Terms & Consent Page
// ════════════════════════════════════════════════════════════════════════════
// Public page explaining SMS notification terms and consent for Telnyx 10DLC compliance

export default function SmsTerms() {
  const { slug } = useParams<{ slug: string }>();
  const [businessInfo, setBusinessInfo] = useState<BusinessInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function loadBusinessInfo() {
      if (!slug) return;

      try {
        setLoading(true);
        const info = await getBusinessInfo(slug);
        setBusinessInfo(info);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to load business info");
      } finally {
        setLoading(false);
      }
    }
    loadBusinessInfo();
  }, [slug]);

  // Brand colors
  const brandPrimary: string = useMemo(() => {
    const extracted = businessInfo?.branding?.extractedColors;
    const firstExtracted = extracted?.[0];
    if (firstExtracted) {
      return firstExtracted;
    }
    return businessInfo?.branding.primaryColor ?? "#273240";
  }, [businessInfo?.branding?.extractedColors, businessInfo?.branding.primaryColor]);

  const blobColors = useMemo(() => {
    const extracted = businessInfo?.branding?.extractedColors;
    return extracted?.length ? generateBlobColors(extracted) : undefined;
  }, [businessInfo?.branding?.extractedColors]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 relative">
      {blobColors && <BlobBackground colors={blobColors} />}

      <div className="relative z-10">
        {/* Header */}
        <header
          className="py-4 px-6 shadow-sm"
          style={{ backgroundColor: brandPrimary }}
        >
          <div className="max-w-4xl mx-auto flex items-center gap-4">
            {businessInfo?.logoUrl && (
              <div className="bg-white rounded-lg p-1.5 shadow-sm">
                <img
                  src={businessInfo.logoUrl}
                  alt={businessInfo.name}
                  className="h-8 w-auto"
                />
              </div>
            )}
            <h1 className="text-xl font-semibold text-white">{businessInfo?.name}</h1>
          </div>
        </header>

        {/* Content */}
        <main className="max-w-3xl mx-auto px-6 py-12">
          <div className="bg-white/80 backdrop-blur-md rounded-2xl shadow-lg border border-white/30 p-8">
            {/* Title */}
            <div className="flex items-center gap-3 mb-6">
              <div
                className="w-12 h-12 rounded-full flex items-center justify-center"
                style={{ backgroundColor: brandPrimary + "20" }}
              >
                <MessageSquare style={{ color: brandPrimary }} size={24} />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">SMS Notification Terms</h1>
                <p className="text-gray-500">Appointment Reminder Consent</p>
              </div>
            </div>

            {/* Introduction */}
            <div className="prose prose-gray max-w-none">
              <p className="text-gray-700 leading-relaxed">
                By providing your phone number and opting in to SMS notifications during the booking process,
                you consent to receive text message appointment reminders from <strong>{businessInfo?.name}</strong>.
              </p>

              {/* What You'll Receive */}
              <h2 className="text-lg font-semibold text-gray-900 mt-8 mb-4 flex items-center gap-2">
                <Phone size={20} style={{ color: brandPrimary }} />
                What You'll Receive
              </h2>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start gap-3">
                  <span
                    className="w-2 h-2 rounded-full mt-2 flex-shrink-0"
                    style={{ backgroundColor: brandPrimary }}
                  />
                  <span><strong>Booking Confirmation:</strong> A text confirming your appointment details after booking.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span
                    className="w-2 h-2 rounded-full mt-2 flex-shrink-0"
                    style={{ backgroundColor: brandPrimary }}
                  />
                  <span><strong>24-Hour Reminder:</strong> A reminder text sent approximately 24 hours before your appointment.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span
                    className="w-2 h-2 rounded-full mt-2 flex-shrink-0"
                    style={{ backgroundColor: brandPrimary }}
                  />
                  <span><strong>1-Hour Reminder:</strong> A final reminder sent approximately 1 hour before your appointment.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span
                    className="w-2 h-2 rounded-full mt-2 flex-shrink-0"
                    style={{ backgroundColor: brandPrimary }}
                  />
                  <span><strong>Cancellation/Reschedule Notices:</strong> Confirmation texts when you cancel or reschedule.</span>
                </li>
              </ul>

              {/* Message Frequency */}
              <h2 className="text-lg font-semibold text-gray-900 mt-8 mb-4 flex items-center gap-2">
                <Clock size={20} style={{ color: brandPrimary }} />
                Message Frequency
              </h2>
              <p className="text-gray-700">
                You will receive approximately <strong>2-4 messages per appointment</strong>.
                Message frequency depends on your appointment schedule and any changes you make to your bookings.
              </p>

              {/* Data & Privacy */}
              <h2 className="text-lg font-semibold text-gray-900 mt-8 mb-4 flex items-center gap-2">
                <Shield size={20} style={{ color: brandPrimary }} />
                Data & Privacy
              </h2>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start gap-3">
                  <span
                    className="w-2 h-2 rounded-full mt-2 flex-shrink-0"
                    style={{ backgroundColor: brandPrimary }}
                  />
                  <span>Your phone number is used solely for appointment-related communications.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span
                    className="w-2 h-2 rounded-full mt-2 flex-shrink-0"
                    style={{ backgroundColor: brandPrimary }}
                  />
                  <span>We do not share your phone number with third parties for marketing purposes.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span
                    className="w-2 h-2 rounded-full mt-2 flex-shrink-0"
                    style={{ backgroundColor: brandPrimary }}
                  />
                  <span>Standard message and data rates may apply based on your mobile carrier plan.</span>
                </li>
              </ul>

              {/* Opt-Out */}
              <h2 className="text-lg font-semibold text-gray-900 mt-8 mb-4 flex items-center gap-2">
                <XCircle size={20} style={{ color: brandPrimary }} />
                How to Opt Out
              </h2>
              <p className="text-gray-700">
                You may opt out of SMS notifications at any time by:
              </p>
              <ul className="space-y-2 text-gray-700 mt-3">
                <li className="flex items-start gap-3">
                  <span
                    className="w-2 h-2 rounded-full mt-2 flex-shrink-0"
                    style={{ backgroundColor: brandPrimary }}
                  />
                  <span>Replying <strong>STOP</strong> to any message you receive from us.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span
                    className="w-2 h-2 rounded-full mt-2 flex-shrink-0"
                    style={{ backgroundColor: brandPrimary }}
                  />
                  <span>Contacting us directly at <strong>{businessInfo?.email}</strong> or <strong>{businessInfo?.phone || "our office"}</strong>.</span>
                </li>
              </ul>
              <p className="text-gray-700 mt-3">
                After opting out, you will receive a confirmation message and will no longer receive SMS notifications
                from {businessInfo?.name}. You can always opt back in during a future booking.
              </p>

              {/* Help */}
              <div className="mt-8 p-4 bg-gray-50 rounded-lg border border-gray-200">
                <p className="text-sm text-gray-600">
                  <strong>Need Help?</strong> If you have questions about our SMS notifications,
                  reply <strong>HELP</strong> to any message or contact us at{" "}
                  <a href={`mailto:${businessInfo?.email}`} className="underline" style={{ color: brandPrimary }}>
                    {businessInfo?.email}
                  </a>
                  .
                </p>
              </div>

              {/* Consent Statement */}
              <div
                className="mt-8 p-4 rounded-lg border-2"
                style={{ borderColor: brandPrimary + "40", backgroundColor: brandPrimary + "10" }}
              >
                <p className="text-sm text-gray-700">
                  <strong>By checking the SMS opt-in box during booking, you:</strong>
                </p>
                <ul className="mt-2 text-sm text-gray-700 space-y-1">
                  <li>- Confirm that the phone number provided is yours or that you have authority to receive texts at that number.</li>
                  <li>- Consent to receive automated appointment reminder text messages from {businessInfo?.name}.</li>
                  <li>- Acknowledge that consent is not a condition of booking an appointment.</li>
                  <li>- Understand that standard message and data rates may apply.</li>
                </ul>
              </div>
            </div>

            {/* Footer */}
            <div className="mt-8 pt-6 border-t border-gray-200 text-center">
              <p className="text-xs text-gray-400">
                Powered by{" "}
                <a
                  href="https://appointmentflo.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline hover:text-gray-600"
                >
                  AppointmentFlo.com
                </a>
              </p>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
